import {
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import usePatientInfoHandlers from '../hooks/usePatientInfoHandlers'
import TextPropertiesEditor from './TextPropertiesEditor'

export default function TemplatePatientDetailsSection({
  prescriptionTemplate,
  prescriptionTemplateHandlers,
  disabled = false,
}) {
  const { setPrescriptionTemplate } = prescriptionTemplateHandlers

  const { handlePatientInfoBooleanChange, createTextPropertySetter } =
    usePatientInfoHandlers(setPrescriptionTemplate)

  const patientInfo = prescriptionTemplate?.patientInfoSectionSettings || {}

  return (
    <>
      {/* QR Code - Simple Checkbox */}
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              id="showQRCode"
              checked={patientInfo?.showQRCode || false}
              onChange={handlePatientInfoBooleanChange}
              disabled={disabled}
            />
          }
          label="Show QR Code"
        />
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Grid item xs={12}>
        {/* Patient Name - Always shown, no boolean */}
        <Accordion
          defaultExpanded
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '& + &': { mt: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
              backgroundColor: 'action.hover',
            }}
          >
            <FormControlLabel
              sx={{
                '.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.87) !important',
                },
              }}
              control={<Checkbox sx={{ opacity: 0 }} disabled={true} />}
              label="Patient Name"
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientNameKey}
                  setData={createTextPropertySetter('patientNameKey')}
                  label="Name Label (Key)"
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientNameVal}
                  setData={createTextPropertySetter('patientNameVal')}
                  label="Name Value"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Age/Gender - Accordion with checkbox in summary */}
        <Accordion
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '& + &': { mt: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
              backgroundColor: 'action.hover',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id="showPatientAgeGender"
                  checked={patientInfo?.showPatientAgeGender ?? true}
                  onChange={handlePatientInfoBooleanChange}
                  disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label="Show Age/Gender"
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientAgeGenderKey}
                  setData={createTextPropertySetter('patientAgeGenderKey')}
                  label="Age/Gender Label (Key)"
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientAgeGenderVal}
                  setData={createTextPropertySetter('patientAgeGenderVal')}
                  label="Age/Gender Value"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Address - Accordion with checkbox in summary */}
        <Accordion
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '& + &': { mt: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
              backgroundColor: 'action.hover',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id="showPatientAddress"
                  checked={patientInfo?.showPatientAddress || false}
                  onChange={handlePatientInfoBooleanChange}
                  disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label="Show Patient Address"
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientAddressKey}
                  setData={createTextPropertySetter('patientAddressKey')}
                  label="Address Label (Key)"
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientAddressVal}
                  setData={createTextPropertySetter('patientAddressVal')}
                  label="Address Value"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Mobile - Accordion with checkbox in summary */}
        <Accordion
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '& + &': { mt: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
              backgroundColor: 'action.hover',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id="showPatientMobile"
                  checked={patientInfo?.showPatientMobile || false}
                  onChange={handlePatientInfoBooleanChange}
                  disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label="Show Patient Mobile"
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientMobileKey}
                  setData={createTextPropertySetter('patientMobileKey')}
                  label="Mobile Label (Key)"
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientMobileVal}
                  setData={createTextPropertySetter('patientMobileVal')}
                  label="Mobile Value"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Email - Accordion with checkbox in summary */}
        <Accordion
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '& + &': { mt: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
              backgroundColor: 'action.hover',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id="showPatientEmail"
                  checked={patientInfo?.showPatientEmail || false}
                  onChange={handlePatientInfoBooleanChange}
                  disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label="Show Patient Email"
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientEmailKey}
                  setData={createTextPropertySetter('patientEmailKey')}
                  label="Email Label (Key)"
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextPropertiesEditor
                  data={patientInfo?.patientEmailVal}
                  setData={createTextPropertySetter('patientEmailVal')}
                  label="Email Value"
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </>
  )
}
